import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import toast from 'react-hot-toast';
import { professionalClientAuthService } from '../../../services/professionalClientAuthService';
import { professionalClientNotificationService } from '../../../services/professionalClientNotificationService';

const MonCompte = ({ professionalClient }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    companyName: professionalClient?.companyName || '',
    contactName: professionalClient?.contactName || '',
    email: professionalClient?.email || '',
    phone: professionalClient?.phone || '',
    siret: '12345678901234',
    adresse: '45 rue de la République, 69001 Lyon',
    numeroTVA: 'FR12345678901'
  });

  const [notificationPrefs, setNotificationPrefs] = useState({
    emailEnabled: true,
    smsEnabled: false,
    notifyShipmentStatus: true,
    notifyInvoiceReady: true,
    notifyCertificateReady: true,
    notifyContainerDeparture: true,
    notifyPromotions: false,
    notificationEmail: professionalClient?.email || '',
    notificationPhone: professionalClient?.phone || ''
  });

  const [loadingPrefs, setLoadingPrefs] = useState(true);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [testingNotification, setTestingNotification] = useState(null);

  const [savedAddresses, setSavedAddresses] = useState([
  { id: 1, label: 'Entrepôt Lyon', adresse: '45 rue de la République, 69001 Lyon' },
  { id: 2, label: 'Siège Paris', adresse: '12 rue du Commerce, 75015 Paris' }]
  );

  const [newAddress, setNewAddress] = useState({ label: '', adresse: '' });
  const [showAddressForm, setShowAddressForm] = useState(false);

  const advisor = {
    nom: 'Namory Traoré',
    photo: "/assets/images/image-1772328678442.png",
    telephone: '06 35 52 82 31',
    email: 'traore@adtexpress.fr'
  };

  const handleSaveProfile = async () => {
    try {
      await professionalClientAuthService?.updateProfile({
        companyName: formData?.companyName,
        contactName: formData?.contactName,
        phone: formData?.phone,
        clientType: professionalClient?.clientType
      });
      toast?.success('Profil mis à jour avec succès');
      setIsEditing(false);
    } catch (error) {
      toast?.error('Erreur lors de la mise à jour du profil');
    }
  };

  useEffect(() => {
    loadNotificationPreferences();
  }, []);

  const loadNotificationPreferences = async () => {
    try {
      setLoadingPrefs(true);
      const prefs = await professionalClientNotificationService?.getNotificationPreferences();
      setNotificationPrefs({
        emailEnabled: prefs?.emailEnabled,
        smsEnabled: prefs?.smsEnabled,
        notifyShipmentStatus: prefs?.notifyShipmentStatus,
        notifyInvoiceReady: prefs?.notifyInvoiceReady,
        notifyCertificateReady: prefs?.notifyCertificateReady,
        notifyContainerDeparture: prefs?.notifyContainerDeparture,
        notifyPromotions: prefs?.notifyPromotions,
        notificationEmail: prefs?.notificationEmail || professionalClient?.email || '',
        notificationPhone: prefs?.notificationPhone || professionalClient?.phone || ''
      });
    } catch (err) {
      console.error('Error loading notification preferences:', err);
      toast?.error('Erreur lors du chargement des préférences');
    } finally {
      setLoadingPrefs(false);
    }
  };

  const handleSaveNotificationPrefs = async () => {
    try {
      setSavingPrefs(true);
      await professionalClientNotificationService?.updateNotificationPreferences(notificationPrefs);
      toast?.success('Préférences de notification enregistrées');
    } catch (err) {
      console.error('Error saving notification preferences:', err);
      toast?.error('Erreur lors de l\'enregistrement');
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleTestNotification = async (channel) => {
    try {
      setTestingNotification(channel);
      await professionalClientNotificationService?.sendTestNotification(channel);
      toast?.success(`Notification de test envoyée par ${channel === 'email' ? 'email' : 'SMS'}`);
    } catch (err) {
      console.error('Error sending test notification:', err);
      toast?.error(err?.message || 'Erreur lors de l\'envoi du test');
    } finally {
      setTestingNotification(null);
    }
  };

  const handleAddAddress = () => {
    if (newAddress?.label && newAddress?.adresse) {
      setSavedAddresses([...savedAddresses, { id: Date.now(), ...newAddress }]);
      setNewAddress({ label: '', adresse: '' });
      setShowAddressForm(false);
      toast?.success('Adresse ajoutée avec succès');
    }
  };

  const handleDeleteAddress = (id) => {
    setSavedAddresses(savedAddresses?.filter((addr) => addr?.id !== id));
    toast?.success('Adresse supprimée');
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Mon compte</h1>

      {/* Company Information */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
            <Icon name="Building" size={24} className="text-green-600" />
            <span>Informations de l'entreprise</span>
          </h2>
          {!isEditing ?
          <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
              <Icon name="Edit" size={16} className="mr-2" />
              Modifier mes informations
            </Button> :

          <div className="flex space-x-2">
              <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
                Annuler
              </Button>
              <Button size="sm" onClick={handleSaveProfile}>
                Enregistrer
              </Button>
            </div>
          }
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nom de l'entreprise</label>
            {isEditing ?
            <Input
              value={formData?.companyName}
              onChange={(e) => setFormData({ ...formData, companyName: e?.target?.value })} /> :


            <p className="text-gray-900 font-medium">{formData?.companyName}</p>
            }
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">SIRET</label>
            <p className="text-gray-900 font-medium">{formData?.siret}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Adresse</label>
            <p className="text-gray-900 font-medium">{formData?.adresse}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Numéro de TVA intracommunautaire</label>
            <p className="text-gray-900 font-medium">{formData?.numeroTVA}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nom du contact principal</label>
            {isEditing ?
            <Input
              value={formData?.contactName}
              onChange={(e) => setFormData({ ...formData, contactName: e?.target?.value })} /> :


            <p className="text-gray-900 font-medium">{formData?.contactName}</p>
            }
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <p className="text-gray-900 font-medium">{formData?.email}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
            {isEditing ?
            <Input
              value={formData?.phone}
              onChange={(e) => setFormData({ ...formData, phone: e?.target?.value })} /> :


            <p className="text-gray-900 font-medium">{formData?.phone}</p>
            }
          </div>
        </div>
      </div>

      {/* Notification Preferences */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Icon name="Bell" size={20} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Préférences de notification</h2>
              <p className="text-sm text-gray-600">Gérez vos alertes SMS et email</p>
            </div>
          </div>
        </div>

        {loadingPrefs ?
        <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div> :

        <div className="space-y-6">
            {/* Notification Channels */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Canaux de notification</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Icon name="Mail" size={20} className="text-blue-600" />
                    <div>
                      <p className="font-medium text-gray-900">Email</p>
                      <p className="text-sm text-gray-600">{notificationPrefs?.notificationEmail}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                      type="checkbox"
                      checked={notificationPrefs?.emailEnabled}
                      onChange={(e) => setNotificationPrefs({ ...notificationPrefs, emailEnabled: e?.target?.checked })}
                      className="sr-only peer" />
                    
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                    </label>
                    {notificationPrefs?.emailEnabled &&
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleTestNotification('email')}
                    disabled={testingNotification === 'email'}>
                    
                        {testingNotification === 'email' ? 'Envoi...' : 'Tester'}
                      </Button>
                  }
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Icon name="Smartphone" size={20} className="text-green-600" />
                    <div>
                      <p className="font-medium text-gray-900">SMS</p>
                      <p className="text-sm text-gray-600">{notificationPrefs?.notificationPhone || 'Non configuré'}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                      type="checkbox"
                      checked={notificationPrefs?.smsEnabled}
                      onChange={(e) => setNotificationPrefs({ ...notificationPrefs, smsEnabled: e?.target?.checked })}
                      className="sr-only peer" />
                    
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                    </label>
                    {notificationPrefs?.smsEnabled && notificationPrefs?.notificationPhone &&
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleTestNotification('sms')}
                    disabled={testingNotification === 'sms'}>
                    
                        {testingNotification === 'sms' ? 'Envoi...' : 'Tester'}
                      </Button>
                  }
                  </div>
                </div>
              </div>
            </div>

            {/* Event Type Preferences */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Types d'alertes</h3>
              <div className="space-y-3">
                <label className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                  <div className="flex items-center space-x-3">
                    <Icon name="Package" size={18} className="text-gray-600" />
                    <span className="text-gray-900">Changement de statut d'expédition</span>
                  </div>
                  <input
                  type="checkbox"
                  checked={notificationPrefs?.notifyShipmentStatus}
                  onChange={(e) => setNotificationPrefs({ ...notificationPrefs, notifyShipmentStatus: e?.target?.checked })}
                  className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500" />
                
                </label>

                <label className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                  <div className="flex items-center space-x-3">
                    <Icon name="FileText" size={18} className="text-gray-600" />
                    <span className="text-gray-900">Facture disponible</span>
                  </div>
                  <input
                  type="checkbox"
                  checked={notificationPrefs?.notifyInvoiceReady}
                  onChange={(e) => setNotificationPrefs({ ...notificationPrefs, notifyInvoiceReady: e?.target?.checked })}
                  className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500" />
                
                </label>

                <label className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                  <div className="flex items-center space-x-3">
                    <Icon name="Award" size={18} className="text-gray-600" />
                    <span className="text-gray-900">Certificat EX1 prêt</span>
                  </div>
                  <input
                  type="checkbox"
                  checked={notificationPrefs?.notifyCertificateReady}
                  onChange={(e) => setNotificationPrefs({ ...notificationPrefs, notifyCertificateReady: e?.target?.checked })}
                  className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500" />
                
                </label>

                <label className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                  <div className="flex items-center space-x-3">
                    <Icon name="Ship" size={18} className="text-gray-600" />
                    <span className="text-gray-900">Départ de conteneur</span>
                  </div>
                  <input
                  type="checkbox"
                  checked={notificationPrefs?.notifyContainerDeparture}
                  onChange={(e) => setNotificationPrefs({ ...notificationPrefs, notifyContainerDeparture: e?.target?.checked })}
                  className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500" />
                
                </label>

                <label className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                  <div className="flex items-center space-x-3">
                    <Icon name="Tag" size={18} className="text-gray-600" />
                    <span className="text-gray-900">Promotions et offres spéciales</span>
                  </div>
                  <input
                  type="checkbox"
                  checked={notificationPrefs?.notifyPromotions}
                  onChange={(e) => setNotificationPrefs({ ...notificationPrefs, notifyPromotions: e?.target?.checked })}
                  className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500" />
                
                </label>
              </div>
            </div>

            {/* Contact Information for Notifications */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Coordonnées de notification</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                label="Email de notification"
                type="email"
                value={notificationPrefs?.notificationEmail}
                onChange={(e) => setNotificationPrefs({ ...notificationPrefs, notificationEmail: e?.target?.value })}
                placeholder="email@exemple.com" />
              
                <Input
                label="Téléphone pour SMS"
                type="tel"
                value={notificationPrefs?.notificationPhone}
                onChange={(e) => setNotificationPrefs({ ...notificationPrefs, notificationPhone: e?.target?.value })}
                placeholder="+33 6 12 34 56 78" />
              
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4">
              <Button
              onClick={handleSaveNotificationPrefs}
              disabled={savingPrefs}
              className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white">
              
                {savingPrefs ? 'Enregistrement...' : 'Enregistrer les préférences'}
              </Button>
            </div>
          </div>
        }
      </div>

      {/* Professional Advisor */}
      <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg shadow-md p-6 text-white">
        <h2 className="text-xl font-bold mb-4 flex items-center space-x-2">
          <Icon name="UserCheck" size={24} />
          <span>Mon conseiller pro</span>
        </h2>
        <div className="flex items-center space-x-4">
          <img
            src={advisor?.photo}
            alt={advisor?.nom}
            className="w-20 h-20 rounded-full border-4 border-white shadow-lg" />
          
          <div className="flex-1">
            <p className="text-2xl font-bold">{advisor?.nom}</p>
            <p className="text-blue-100 mt-1">Directeur & Fondateur ADT Express</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          <div className="bg-white bg-opacity-20 rounded-lg p-3">
            <p className="text-sm text-blue-100">Ligne directe</p>
            <p className="font-bold">{advisor?.telephone}</p>
          </div>
          <div className="bg-white bg-opacity-20 rounded-lg p-3">
            <p className="text-sm text-blue-100">Email direct</p>
            <p className="font-bold">{advisor?.email}</p>
          </div>
        </div>
        <Button className="w-full mt-4 bg-white text-blue-600 hover:bg-gray-100">
          <Icon name="Send" size={16} className="mr-2" />
          Envoyer un message
        </Button>
      </div>

      {/* Saved Addresses */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
            <Icon name="MapPin" size={24} className="text-purple-600" />
            <span>Adresses enregistrées</span>
          </h2>
          <Button size="sm" variant="outline" onClick={() => setShowAddressForm(!showAddressForm)}>
            <Icon name="Plus" size={16} className="mr-2" />
            Ajouter une adresse
          </Button>
        </div>

        {showAddressForm &&
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="space-y-3">
              <Input
              placeholder="Libellé (ex: Entrepôt Lyon)"
              value={newAddress?.label}
              onChange={(e) => setNewAddress({ ...newAddress, label: e?.target?.value })} />
            
              <Input
              placeholder="Adresse complète"
              value={newAddress?.adresse}
              onChange={(e) => setNewAddress({ ...newAddress, adresse: e?.target?.value })} />
            
              <div className="flex space-x-2">
                <Button size="sm" onClick={handleAddAddress}>
                  Enregistrer
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowAddressForm(false)}>
                  Annuler
                </Button>
              </div>
            </div>
          </div>
        }

        <div className="space-y-3">
          {savedAddresses?.map((addr) =>
          <div key={addr?.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
              <div>
                <p className="font-bold text-gray-900">{addr?.label}</p>
                <p className="text-sm text-gray-600">{addr?.adresse}</p>
              </div>
              <button
              onClick={() => handleDeleteAddress(addr?.id)}
              className="text-red-600 hover:text-red-800">
              
                <Icon name="Trash2" size={20} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>);

};

export default MonCompte;